import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import cn from 'classnames';
import moment from 'moment';
import './CollectionSearch.scss';
import './CollectionSearchResult.scss';
import './ItemSearchResult.scss';

import { getCreatorProfileUrl, getCollectionUrlFromId, getPrettyPriceDisplay } from '../../helpers/helpers';
import ShopmyImage from '../General/ShopmyImage';
import { searchCollectionAnalysis } from '../../api/collection_analysis';

const RESULT_TABS = [
  { id: 'collections', label: 'Collections' },
  { id: 'items', label: 'Items' },
  { id: 'blended', label: 'Blended' }
];

// Vibe-oriented defaults — admin steppers own these; Search never resets them.
const DEFAULT_ITEM_EMBED_WEIGHTS = { raw: 0, brief: 0.3, signature: 1, attributes: 0.5, taste: 0.5 };
const DEFAULT_BLEND_WEIGHTS = { item: 1, vibe: 1 };
// 1 = server default multipliers, 0 = axis off, up to 2 = doubled effect.
const DEFAULT_RANK_KNOBS = { dtc: 1, stock: 1 };
// Blend weights for the personalized collection query vector: text = the raw-query+expansion
// composite embedding, taste = the shopper's taste centroid. Normalized server-side by sum.
const DEFAULT_COLLECTION_EMBED_WEIGHTS = { text: 1, taste: 0.25 };

const CollectionSearch = props => {
  const { passingScore } = props;
  const [query, setQuery] = useState('');
  const [tasteUserInput, setTasteUserInput] = useState('');
  const [tasteUser, setTasteUser] = useState(null);
  const [results, setResults] = useState(null);
  const [collectionsError, setCollectionsError] = useState(null);
  const [personalizedCollections, setPersonalizedCollections] = useState(null);
  const [items, setItems] = useState(null);
  const [expandedItems, setExpandedItems] = useState(null);
  const [personalizedItems, setPersonalizedItems] = useState(null);
  const [personalizedExpandedItems, setPersonalizedExpandedItems] = useState(null);
  const [itemPersonalization, setItemPersonalization] = useState(null);
  const [blended, setBlended] = useState(null);
  const [blendWeights, setBlendWeights] = useState(DEFAULT_BLEND_WEIGHTS);
  // When false, omit blend params so the server applies intent dampening (vibe → item 0.4, etc).
  const [blendWeightsDirty, setBlendWeightsDirty] = useState(false);
  const [expandedQuery, setExpandedQuery] = useState(null);
  const [queryFacets, setQueryFacets] = useState(null);
  const [intent, setIntent] = useState(null);
  const [itemExpansion, setItemExpansion] = useState(null);
  const [queryColors, setQueryColors] = useState([]);
  const [timing, setTiming] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [personalizeSearch, setPersonalizeSearch] = useState(true);
  const [itemRankPass, setItemRankPass] = useState(true);
  const [rankKnobs, setRankKnobs] = useState(DEFAULT_RANK_KNOBS);
  const [categorySpacing, setCategorySpacing] = useState(true);
  const [affinityStrength, setAffinityStrength] = useState(1);
  const [collectionAffinityKnobs, setCollectionAffinityKnobs] = useState({ circle: 1, neighbor: 1 });
  const [collectionEmbedWeights, setCollectionEmbedWeights] = useState(DEFAULT_COLLECTION_EMBED_WEIGHTS);
  const [expansionsOpen, setExpansionsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('items');
  const [itemEmbedWeights, setItemEmbedWeights] = useState(DEFAULT_ITEM_EMBED_WEIGHTS);

  const runSearch = async ({
    nextPersonalizeSearch = personalizeSearch,
    nextItemRankPass = itemRankPass,
    nextRankKnobs = rankKnobs,
    nextCategorySpacing = categorySpacing,
    nextAffinityStrength = affinityStrength,
    nextCollectionAffinityKnobs = collectionAffinityKnobs,
    nextCollectionEmbedWeights = collectionEmbedWeights,
    nextWeights = itemEmbedWeights,
    nextBlendWeights = blendWeights,
    nextBlendWeightsDirty = blendWeightsDirty
  } = {}) => {
    if (!query.trim() || isSearching) return;
    setIsSearching(true);
    const startedAt = performance.now();
    try {
      const requestedTasteUser = tasteUserInput.trim();
      const shouldPersonalize = nextPersonalizeSearch && !!requestedTasteUser;
      const data = await searchCollectionAnalysis({
        query: query.trim(),
        itemRawWeight: nextWeights.raw,
        itemBriefWeight: nextWeights.brief,
        itemSignatureWeight: nextWeights.signature,
        itemAttributeWeight: nextWeights.attributes,
        itemTasteWeight: nextWeights.taste,
        itemRankPass: nextItemRankPass ? 1 : 0,
        rankDtcStrength: nextRankKnobs.dtc,
        rankStockStrength: nextRankKnobs.stock,
        affinityStrength: nextAffinityStrength,
        categorySpacing: nextCategorySpacing ? 'on' : 'off',
        circleStrength: nextCollectionAffinityKnobs.circle,
        neighborStrength: nextCollectionAffinityKnobs.neighbor,
        collectionTextWeight: nextCollectionEmbedWeights.text,
        collectionTasteWeight: nextCollectionEmbedWeights.taste,
        ...(nextBlendWeightsDirty
          ? { blendItemWeight: nextBlendWeights.item, blendVibeWeight: nextBlendWeights.vibe }
          : {}),
        ...(shouldPersonalize ? { tasteUser: requestedTasteUser, personalize: true } : { personalize: false })
      });
      if (shouldPersonalize && !data.tasteUser) window.ALERT.error(`No user found matching "${requestedTasteUser}".`);
      setResults(data.results || []);
      setCollectionsError(data.collectionsError || null);
      setPersonalizedCollections(data.personalizedCollections || null);
      setItems(data.items || null);
      setExpandedItems(data.expandedItems || null);
      setPersonalizedItems(data.personalizedItems || null);
      setPersonalizedExpandedItems(data.personalizedExpandedItems || null);
      setItemPersonalization(data.itemPersonalization || null);
      setTasteUser(data.tasteUser || null);
      setBlended(data.blended || []);
      // Sync steppers to intent-resolved blend weights until the admin edits them.
      if (!nextBlendWeightsDirty && data.blendWeights) setBlendWeights(data.blendWeights);
      setExpandedQuery(data.expandedQuery || null);
      setQueryFacets(data.queryFacets || null);
      setIntent(data.intent || null);
      setItemExpansion(data.itemExpansion || null);
      setQueryColors(data.queryColors || []);
      setTiming({ roundTripMs: Math.round(performance.now() - startedAt), ...data.timings });
    } catch (error) {
      const serverMsg = error?.data?.msg || error?.data?.error || error?.response?.data?.msg || error?.message;
      window.ALERT.error(serverMsg || 'Search failed — make sure the collection search indexes have been embedded.');
    }
    setIsSearching(false);
  };

  const stepValue = (value, delta, max) => Math.round(Math.min(max, Math.max(0, (value || 0) + delta)) * 100) / 100;
  const bumpKeyedKnob = (setKnobs, max) => (key, delta) => setKnobs(prev => ({ ...prev, [key]: stepValue(prev[key], delta, max) }));

  const bumpEmbedWeight = bumpKeyedKnob(setItemEmbedWeights, 1);
  const bumpBlendWeight = (key, delta) => {
    setBlendWeightsDirty(true);
    bumpKeyedKnob(setBlendWeights, 1)(key, delta);
  };
  const bumpRankKnob = bumpKeyedKnob(setRankKnobs, 2);
  const bumpAffinityStrength = delta => setAffinityStrength(prev => stepValue(prev, delta, 2));
  const bumpCollectionAffinityKnob = bumpKeyedKnob(setCollectionAffinityKnobs, 2);
  const bumpCollectionEmbedWeight = bumpKeyedKnob(setCollectionEmbedWeights, 1);

  const toggleCategorySpacing = () => {
    if (isSearching) return;
    const next = !categorySpacing;
    setCategorySpacing(next);
    if (query.trim() && results) {
      runSearch({ nextCategorySpacing: next });
    }
  };

  const resetWeightDefaults = () => {
    setItemEmbedWeights(DEFAULT_ITEM_EMBED_WEIGHTS);
    setBlendWeights(DEFAULT_BLEND_WEIGHTS);
    setBlendWeightsDirty(false);
    setRankKnobs(DEFAULT_RANK_KNOBS);
    setCategorySpacing(true);
    setAffinityStrength(1);
    setCollectionAffinityKnobs({ circle: 1, neighbor: 1 });
    setCollectionEmbedWeights(DEFAULT_COLLECTION_EMBED_WEIGHTS);
  };

  const togglePersonalizeSearch = () => {
    if (isSearching || !tasteUserInput.trim()) return;
    const next = !personalizeSearch;
    setPersonalizeSearch(next);
    if (query.trim() && results) {
      runSearch({ nextPersonalizeSearch: next });
    }
  };

  const toggleItemRankPass = () => {
    if (isSearching) return;
    const next = !itemRankPass;
    setItemRankPass(next);
    if (query.trim() && results) {
      runSearch({ nextItemRankPass: next });
    }
  };

  // Prefer a successful leg with matches; skip soft-failed `{ results: [], error }` objects.
  const pickItemLeg = (...legs) => {
    for (const leg of legs) {
      if (!leg || leg.error) continue;
      if (leg.results?.length) return leg;
    }
    return legs.find(leg => leg && !leg.error) || legs.find(Boolean) || null;
  };
  const tasteInput = tasteUserInput.trim();
  const tasteUserMatchesInput =
    !!tasteUser &&
    !!tasteInput &&
    (String(tasteUser.id) === tasteInput ||
      (!!tasteUser.username && tasteUser.username.toLowerCase() === tasteInput.toLowerCase()));
  const hasPersonalized = personalizeSearch && tasteUserMatchesInput;
  const baselineItemLeg = hasPersonalized ? pickItemLeg(personalizedItems, items) : items;
  const expandedItemLeg = hasPersonalized ? pickItemLeg(personalizedExpandedItems, expandedItems, items) : expandedItems;
  const baselineItemResults = baselineItemLeg?.results || [];
  const expandedItemResults = expandedItemLeg?.results || [];
  const personalizedCollectionResults = personalizedCollections?.error ? [] : personalizedCollections?.results || [];
  const baselinePositionByProductId = {};
  baselineItemResults.forEach((item, index) => (baselinePositionByProductId[item.Product_id] = index + 1));
  const baselinePositionByCollectionId = {};
  (results || []).forEach((result, index) => (baselinePositionByCollectionId[result.Collection_id] = index + 1));
  const collectionsById = _.keyBy(
    [...(results || []), ...personalizedCollectionResults],
    'Collection_id'
  );
  // Blend can pull from any item leg; keep all hydrated ids available for card lookup.
  const itemsByProductId = _.keyBy(
    [
      ...(items?.results || []),
      ...(expandedItems?.results || []),
      ...(personalizedItems?.results || []),
      ...(personalizedExpandedItems?.results || [])
    ],
    'Product_id'
  );
  const blendedEntries = (blended || []).map(entry => ({
    ...entry,
    match: entry.type === 'collection' ? collectionsById[entry.id] : itemsByProductId[entry.id]
  }));
  const hasExpansionPanels = results && (expandedQuery || itemExpansion?.productBrief || itemExpansion?.expandedQuery);
  const itemIntent = itemExpansion?.intent || intent;
  const hasResults =
    results && (!!results.length || !!baselineItemResults.length || !!expandedItemResults.length || !!personalizedCollectionResults.length);

  return (
    <div className='collection-search-outer'>
      <div className='collection-search-controls'>
        <input
          type='text'
          value={query}
          onChange={({ target }) => setQuery(target.value)}
          onKeyDown={({ key }) => {
            if (key === 'Enter') runSearch();
          }}
          placeholder='Try a shopper query, e.g. "wedding guest dress" or "euro summer"'
        />
        <input
          className='taste-user-input'
          type='text'
          value={tasteUserInput}
          onChange={({ target }) => setTasteUserInput(target.value)}
          onKeyDown={({ key }) => {
            if (key === 'Enter') runSearch();
          }}
          placeholder='User ID or username'
        />
        <button type='button' disabled={isSearching || !query.trim()} onClick={() => runSearch()}>
          {isSearching ? 'Searching...' : 'Search'}
        </button>
      </div>

      <div className='search-controls-layout'>
        <div className='search-control-bars'>
          <div className='control-row'>
            <ControlGroup label='Personalize'>
              <Pill
                active={!personalizeSearch || !tasteUserInput.trim()}
                disabled={!tasteUserInput.trim() || isSearching}
                onClick={() => personalizeSearch && togglePersonalizeSearch()}
              >
                off
              </Pill>
              <Pill
                active={personalizeSearch && !!tasteUserInput.trim()}
                disabled={!tasteUserInput.trim() || isSearching}
                onClick={() => !personalizeSearch && togglePersonalizeSearch()}
              >
                on
              </Pill>
            </ControlGroup>
            <span className='control-pipe' />
            {hasExpansionPanels && (
              <>
                <ControlGroup label='Expansions'>
                  <Pill active={!expansionsOpen} onClick={() => setExpansionsOpen(false)}>
                    hide
                  </Pill>
                  <Pill active={expansionsOpen} onClick={() => setExpansionsOpen(true)}>
                    show
                  </Pill>
                </ControlGroup>
                <span className='control-pipe' />
              </>
            )}
            <ControlGroup label='Item embed weights'>
              {[
                { key: 'raw', label: 'raw' },
                { key: 'brief', label: 'brief' },
                { key: 'signature', label: 'signature' },
                { key: 'attributes', label: 'attrs' },
                { key: 'taste', label: 'taste', disabled: !(personalizeSearch && tasteUserInput.trim()) }
              ].map(({ key, label, disabled }) => (
                <WeightStepper
                  key={key}
                  label={label}
                  value={itemEmbedWeights[key]}
                  disabled={disabled || isSearching}
                  onBump={delta => bumpEmbedWeight(key, delta)}
                />
              ))}
            </ControlGroup>
            <span className='control-pipe' />
            <ControlGroup label='Item brand affinity'>
              <WeightStepper
                label='strength'
                value={affinityStrength}
                max={2}
                disabled={!(personalizeSearch && tasteUserInput.trim()) || isSearching}
                onBump={bumpAffinityStrength}
              />
            </ControlGroup>
          </div>
          <div className='control-row'>
            <ControlGroup label='Coll embed weights'>
              {[
                { key: 'text', label: 'text' },
                { key: 'taste', label: 'taste' }
              ].map(({ key, label }) => (
                <WeightStepper
                  key={key}
                  label={label}
                  value={collectionEmbedWeights[key]}
                  max={1}
                  disabled={!(personalizeSearch && tasteUserInput.trim()) || isSearching}
                  onBump={delta => bumpCollectionEmbedWeight(key, delta)}
                />
              ))}
            </ControlGroup>
            <span className='control-pipe' />
            <ControlGroup label='Coll creator affinity'>
              {['circle', 'neighbor'].map(key => (
                <WeightStepper
                  key={key}
                  label={key}
                  value={collectionAffinityKnobs[key]}
                  max={2}
                  disabled={!(personalizeSearch && tasteUserInput.trim()) || isSearching}
                  onBump={delta => bumpCollectionAffinityKnob(key, delta)}
                />
              ))}
            </ControlGroup>
            <span className='control-pipe' />
            <ControlGroup label='Blend weights'>
              <WeightStepper
                label='items'
                value={blendWeights.item}
                disabled={isSearching}
                onBump={delta => bumpBlendWeight('item', delta)}
              />
              <WeightStepper
                label='vibe'
                value={blendWeights.vibe}
                disabled={isSearching}
                onBump={delta => bumpBlendWeight('vibe', delta)}
              />
            </ControlGroup>
          </div>
          <div className='control-row'>
            <ControlGroup label='Rank'>
              <Pill active={!itemRankPass} disabled={isSearching} onClick={() => itemRankPass && toggleItemRankPass()}>
                off
              </Pill>
              <Pill active={itemRankPass} disabled={isSearching} onClick={() => !itemRankPass && toggleItemRankPass()}>
                on
              </Pill>
            </ControlGroup>
            <span className='control-pipe' />
            <ControlGroup label='Weights'>
              {[
                { key: 'dtc', label: 'dtc' },
                { key: 'stock', label: 'stock' }
              ].map(({ key, label }) => (
                <WeightStepper
                  key={key}
                  label={label}
                  value={rankKnobs[key]}
                  max={2}
                  disabled={!itemRankPass || isSearching}
                  onBump={delta => bumpRankKnob(key, delta)}
                />
              ))}
            </ControlGroup>
            <span className='control-pipe' />
            <ControlGroup label='Cat spacing'>
              <Pill active={!categorySpacing} disabled={!itemRankPass || isSearching} onClick={() => categorySpacing && toggleCategorySpacing()}>
                off
              </Pill>
              <Pill active={categorySpacing} disabled={!itemRankPass || isSearching} onClick={() => !categorySpacing && toggleCategorySpacing()}>
                on
              </Pill>
            </ControlGroup>
            <button type='button' className='control-reset' disabled={isSearching} onClick={resetWeightDefaults}>
              Reset defaults
            </button>
          </div>
          {tasteUser && (
            <div className='control-row'>
              <ControlGroup label='Taste vector'>
                <span className={cn('control-chip', tasteUser.lean || 'neutral')}>
                  {tasteUser.hasTasteEmbedding
                    ? `${formatLean(tasteUser.lean)} · ${Math.round((tasteUser.mensShare || 0) * 100)}% m`
                    : 'inactive'}
                </span>
              </ControlGroup>
              {hasPersonalized && itemPersonalization && (
                <>
                  <span className='control-pipe' />
                  <ControlGroup label='Item affinity'>
                    <span className='control-chip neutral'>
                      {itemPersonalization.priceBand
                        ? `$${Math.round(itemPersonalization.priceBand.low)}–$${Math.round(itemPersonalization.priceBand.high)} band`
                        : 'no price band'}
                      {` · ${itemPersonalization.purchasedBrandCount || 0} bought brands · ${itemPersonalization.savedBrandCount || 0} saved`}
                    </span>
                  </ControlGroup>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {results && timing && (
        <div className='collection-search-timing'>
          <span className='total'>{(timing.roundTripMs / 1000).toFixed(2)}s</span>
          {timing.expansionMs != null && <span>collection expansion {timing.expansionMs}ms</span>}
          {timing.collectionsMs != null && <span>collection search {timing.collectionsMs}ms</span>}
          {timing.itemExpansionMs != null && <span>item expansion {timing.itemExpansionMs}ms</span>}
          {timing.itemsMs != null && <span>item search {timing.itemsMs}ms</span>}
          {timing.expandedItemsMs != null && <span>item search (expanded) {timing.expandedItemsMs}ms</span>}
          {timing.totalMs != null && <span>server total {timing.totalMs}ms</span>}
          {itemIntent && <span className={cn('intent-chip', itemIntent)}>{itemIntent} intent</span>}
          {tasteUser && (
            <span className='summary-user'>
              {tasteUser.name || `@${tasteUser.username}`} · #{tasteUser.id}
              {tasteUser.numHearted != null ? ` · ${tasteUser.numHearted} hearts` : ''}
            </span>
          )}
        </div>
      )}

      {results && (
        <div className='collection-search-tabs'>
          {RESULT_TABS.map(tab => (
            <button
              key={tab.id}
              type='button'
              className={cn('search-tab', { active: activeTab === tab.id })}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {hasExpansionPanels && expansionsOpen && (
        <div className='collection-search-expansions'>
          <ExpansionPanel
            title='Collection expansion'
            intent={intent}
            extraChips={queryColors.length ? [`color: ${queryColors.join(', ')}`] : []}
          >
            <CollectionExpansionBody query={query.trim()} expandedQuery={expandedQuery} queryFacets={queryFacets} />
          </ExpansionPanel>
          <ExpansionPanel title='Item expansion' intent={itemExpansion?.intent}>
            <ItemExpansionBody query={query.trim()} expansion={itemExpansion} />
          </ExpansionPanel>
        </div>
      )}

      {results && !hasResults && <div className='collection-search-empty'>No matches — the index may not have vectors yet.</div>}

      {hasResults && activeTab === 'collections' && (
        <div className={cn('collection-search-compare', 'tab-compare', { 'with-personalized-collections': !!personalizedCollections })}>
          <div className='compare-column'>
            <div className='compare-header'>
              <span className='compare-title'>Collections · {results.length}</span>
              {timing?.collectionsMs != null && <span className='compare-timing'>{(timing.collectionsMs / 1000).toFixed(2)}s</span>}
            </div>
            {collectionsError && <div className='compare-empty'>Collections search unavailable: {collectionsError}</div>}
            {!results.length && !collectionsError && <div className='compare-empty'>No collection matches.</div>}
            {results.map(result => (
              <SearchResult key={result.Collection_id} result={result} passingScore={passingScore} />
            ))}
          </div>
          {personalizedCollections && (
            <div className='compare-column'>
              <div className='compare-header'>
                <span className='compare-title'>As {tasteUser ? tasteUser.name || `@${tasteUser.username}` : 'user'}</span>
              </div>
              <div className='compare-note'>
                {personalizedCollections.personalizedQuery
                  ? `Searched as “${personalizedCollections.personalizedQuery}”`
                  : 'Same query as baseline'}
                {personalizedCollections.creatorAffinity &&
                  ` · circle ${personalizedCollections.creatorAffinity.circleCount} · neighbors ${personalizedCollections.creatorAffinity.neighborCount}`}
                {personalizedCollections.tasteBlended && ' · taste-blended'}
              </div>
              {personalizedCollections.error && (
                <div className='compare-empty'>Collections search unavailable: {personalizedCollections.error}</div>
              )}
              {!personalizedCollectionResults.length && !personalizedCollections.error && (
                <div className='compare-empty'>No collection matches.</div>
              )}
              {personalizedCollectionResults.map((result, index) => {
                const baselinePosition = baselinePositionByCollectionId[result.Collection_id];
                const movement = baselinePosition == null ? 'new' : baselinePosition - (index + 1);
                return <SearchResult key={result.Collection_id} result={result} passingScore={passingScore} movement={movement} />;
              })}
            </div>
          )}
        </div>
      )}

      {hasResults && activeTab === 'items' && (
        <div className='items-tab'>
          <div className='collection-search-compare tab-compare items-compare'>
            <div className='compare-column'>
              <div className='compare-header'>
                <span className='compare-title'>
                  Without expansion · {baselineItemResults.length}
                  {hasPersonalized && ` · ${baselineItemResults.filter(item => item.injected).length} injected`}
                </span>
                {timing?.itemsMs != null && <span className='compare-timing'>{(timing.itemsMs / 1000).toFixed(2)}s</span>}
                {baselineItemLeg?.searchVariantUsed && (
                  <span className='compare-variant'>{baselineItemLeg.searchVariantUsed}</span>
                )}
              </div>
              {!baselineItemResults.length && (
                <div className='compare-empty'>{baselineItemLeg?.error || 'No item matches.'}</div>
              )}
              {baselineItemResults.map((item, index) => (
                <ItemResult key={item.Product_id} item={item} position={index + 1} />
              ))}
            </div>
            <div className='compare-column'>
              <div className='compare-header'>
                <span className='compare-title'>
                  With expansion · {expandedItemResults.length}
                  {hasPersonalized && ` · ${expandedItemResults.filter(item => item.injected).length} injected`}
                </span>
                {timing?.expandedItemsMs != null && (
                  <span className='compare-timing'>{(timing.expandedItemsMs / 1000).toFixed(2)}s</span>
                )}
                {expandedItemLeg?.searchVariantUsed && (
                  <span className='compare-variant applied'>{expandedItemLeg.searchVariantUsed}</span>
                )}
              </div>
              {!expandedItemResults.length && (
                <div className='compare-empty'>{expandedItemLeg?.error || 'No item matches.'}</div>
              )}
              {expandedItemResults.map((item, index) => {
                const baselinePosition = baselinePositionByProductId[item.Product_id];
                const movement = baselinePosition == null ? 'new' : baselinePosition - (index + 1);
                return <ItemResult key={item.Product_id} item={item} position={index + 1} movement={movement} />;
              })}
            </div>
          </div>
        </div>
      )}

      {hasResults && activeTab === 'blended' && (
        <div className='blended-tab'>
          <div className='blended-timing-header'>
            <div className='blended-timing-title'>
              blended: {blendedEntries.filter(entry => entry.match).length} results
            </div>
            {(timing?.collectionsMs != null || timing?.expandedItemsMs != null) && (
              <div className='blended-timing-meta'>
                {[
                  timing?.collectionsMs != null && `Collections: ${(timing.collectionsMs / 1000).toFixed(2)}s`,
                  timing?.expandedItemsMs != null && `Items: ${(timing.expandedItemsMs / 1000).toFixed(2)}s`
                ]
                  .filter(Boolean)
                  .join(' | ')}
              </div>
            )}
          </div>
          {collectionsError && (
            <div className='blended-collections-note'>
              Collections unavailable ({collectionsError}) — mobile preview is items-only until collection search recovers.
            </div>
          )}
          {!collectionsError && !results.length && (
            <div className='blended-collections-note'>No collection matches to weave in for this query.</div>
          )}
          <div className='blended-phone'>
            <div className='blended-phone-chrome'>Search</div>
            <div className='blended-phone-scroll'>
              {!blendedEntries.length && <div className='compare-empty'>No results passed the blend thresholds.</div>}
              {blendedEntries.map((entry, index) => (
                <BlendedResult
                  key={`${entry.type}-${entry.id}`}
                  entry={entry}
                  rank={index + 1}
                  compact
                  blendWeights={blendWeights}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ControlGroup = ({ label, children }) => (
  <div className='control-group'>
    <span className='control-group-label'>{label}</span>
    <div className='control-group-body'>{children}</div>
  </div>
);

const Pill = ({ active, disabled, onClick, children }) => (
  <button type='button' className={cn('control-pill', { active, disabled })} disabled={disabled} onClick={onClick}>
    {children}
  </button>
);

const WeightStepper = ({ label, value, disabled, onBump, max = 1 }) => (
  <div className={cn('weight-stepper', { disabled })}>
    <span className='weight-stepper-label'>{label}</span>
    <input type='text' readOnly value={value.toFixed(2)} disabled={disabled} />
    <span className='weight-stepper-arrows'>
      <button type='button' disabled={disabled || value >= max} onClick={() => onBump(0.1)} aria-label={`Increase ${label}`}>
        ▲
      </button>
      <button type='button' disabled={disabled || value <= 0} onClick={() => onBump(-0.1)} aria-label={`Decrease ${label}`}>
        ▼
      </button>
    </span>
  </div>
);

const ExpansionPanel = props => {
  const { title, intent, extraChips, badge, children } = props;
  return (
    <div className='expansion-panel'>
      <div className='expansion-panel-header'>
        <span className='expansion-panel-title'>{title}</span>
        {intent && <span className={cn('intent-chip', intent)}>{intent}</span>}
        {(extraChips || []).map(chip => (
          <span key={chip} className='intent-chip color'>
            {chip}
          </span>
        ))}
        {badge && <span className='intent-chip applied'>{badge}</span>}
      </div>
      <div className='expansion-panel-body'>{children}</div>
    </div>
  );
};

const formatExpansionList = values => (values || []).filter(Boolean).join(' | ');

const getCollectionExpansionDescription = expandedQuery => {
  if (!expandedQuery) return null;
  const match = expandedQuery.match(/^COLLECTION:\s*(.+)$/m);
  return (match?.[1] || expandedQuery).trim() || null;
};

const ExpansionFields = props => {
  const { query, brief, attributeGroups } = props;
  return (
    <div className='item-expansion-body'>
      <div className='expansion-field'>
        <span className='expansion-field-label'>Raw text</span>
        <span className='expansion-text'>{query || '—'}</span>
      </div>
      <div className='expansion-field'>
        <span className='expansion-field-label'>Brief</span>
        <span className='expansion-text'>{brief || '—'}</span>
      </div>
      {attributeGroups.map(({ label, values }) => (
        <div key={label} className='expansion-field'>
          <span className='expansion-field-label'>{label}</span>
          <span className='expansion-text'>{formatExpansionList(values)}</span>
        </div>
      ))}
    </div>
  );
};

const CollectionExpansionBody = props => {
  const { query, expandedQuery, queryFacets } = props;
  const description = getCollectionExpansionDescription(expandedQuery);
  if (!description && !queryFacets) {
    return <span className='expansion-empty'>No collection expansion.</span>;
  }
  const attributeGroups = [
    { label: 'ITEMS', values: queryFacets?.items },
    { label: 'OCCASION', values: queryFacets?.occasion },
    { label: 'AESTHETIC', values: queryFacets?.aesthetic },
    { label: 'SEASON', values: queryFacets?.season }
  ].filter(group => group.values?.length);

  return <ExpansionFields query={query} brief={description} attributeGroups={attributeGroups} />;
};

const ItemExpansionBody = props => {
  const { query, expansion } = props;
  if (!expansion?.productBrief && !expansion?.expandedQuery) {
    return <span className='expansion-empty'>No item expansion.</span>;
  }
  const attributeGroups = [
    { label: 'ITEMS', values: expansion.signatureItems },
    { label: 'COLORS', values: expansion.attributes?.colors },
    { label: 'MATERIALS', values: expansion.attributes?.materials },
    { label: 'BRANDS', values: expansion.attributes?.brands },
    { label: 'CATEGORIES', values: expansion.attributes?.categoryHints },
    { label: 'AESTHETIC', values: expansion.aesthetic },
    { label: 'SEASON', values: expansion.season },
    ...(expansion.priceBand ? [{ label: 'PRICE', values: [expansion.priceBand] }] : [])
  ].filter(group => group.values?.length);

  return <ExpansionFields query={query} brief={expansion.productBrief} attributeGroups={attributeGroups} />;
};

const getSafeExternalUrl = url => (/^https?:\/\//i.test(url || '') ? url : null);

// Tones follow the multiplier direction; spacing reasons reorder without touching the score.
const RANK_REASON_CHIPS = {
  dtc_in_stock: { label: 'DTC in stock', tone: 'boost' },
  dtc_unknown_stock: { label: 'DTC stock unknown', tone: 'boost' },
  dtc_out_of_stock: { label: 'DTC out of stock', tone: 'demote' },
  retailer_in_stock: { label: 'retailer in stock', tone: 'demote' },
  retailer_out_of_stock: { label: 'retailer out of stock', tone: 'demote' },
  non_dtc_unknown_stock: { label: 'stock unknown', tone: 'demote' },
  no_image: { label: 'no image', tone: 'demote' },
  brand_spaced: { label: 'brand spaced', tone: 'neutral' },
  brand_adjacent_forced: { label: 'brand adjacent', tone: 'neutral' },
  category_spaced: { label: 'category spaced', tone: 'neutral' },
  category_adjacent_forced: { label: 'category adjacent', tone: 'neutral' }
};

const formatLean = lean => (lean === 'mens' ? "leans men's" : lean === 'womens' ? "leans women's" : 'no lean');

const BlendedResult = props => {
  const { entry, rank, compact, blendWeights } = props;
  const { type, blendScore, typeRank, typeStrength, intentWeight, match } = entry;
  const isCollection = type === 'collection';
  if (!match) {
    return (
      <div className={cn('blended-search-result', { compact })}>
        <div className='blended-media'>
          <div className='blended-image-placeholder' />
          {compact && <span className='blended-rank'>#{rank}</span>}
          {compact && <span className={cn('blended-type-chip', type)}>{isCollection ? 'Collection' : 'Item'}</span>}
        </div>
        <div className='blended-main'>
          <span className='blended-name'>
            Missing {isCollection ? 'collection' : 'item'} #{entry.id}
          </span>
        </div>
      </div>
    );
  }
  const pinImage = isCollection ? match.previewPins?.find(pin => pin.image)?.image : null;
  const image = isCollection ? match.collageUrl || match.collectionImage || pinImage : match.image;
  const name = isCollection ? match.collectionName || `Collection #${match.Collection_id}` : match.title || 'Untitled';
  const link = isCollection ? getCollectionUrlFromId(match.Collection_id) : getSafeExternalUrl(match.link);
  const subtitle = isCollection
    ? match.creatorName || match.creatorUsername
    : [match.brand, match.price != null ? getPrettyPriceDisplay(match.price) : null].filter(Boolean).join(' · ');
  const typeWeight = isCollection ? blendWeights?.vibe ?? intentWeight : blendWeights?.item ?? intentWeight;
  const typeWeightLabel = isCollection ? 'vibe' : 'items';
  const signals = compact
    ? [
        { label: 'blend', value: blendScore != null ? blendScore.toFixed(3) : '—', isPrimary: true },
        ...(typeWeight != null
          ? [{ label: typeWeightLabel, value: `×${typeWeight}`, isPrimary: typeWeight < 1 }]
          : [])
      ]
    : [
        { label: 'blend', value: blendScore != null ? blendScore.toFixed(3) : '—', isPrimary: true },
        { label: isCollection ? 'of collections' : 'of items', value: `#${typeRank}` },
        { label: 'type strength', value: typeStrength != null ? `${Math.round(typeStrength * 100)}%` : '—' },
        ...(typeWeight != null ? [{ label: typeWeightLabel, value: `×${typeWeight}`, isPrimary: typeWeight < 1 }] : [])
      ];
  const media = image ? <ShopmyImage source={image} alt={name} /> : <div className='blended-image-placeholder' />;
  const titleNode = link ? (
    <a className='blended-name' href={link} target='_blank' rel='noopener noreferrer'>
      {name}
    </a>
  ) : (
    <span className='blended-name'>{name}</span>
  );
  const signalNodes = (
    <div className='blended-signals'>
      {signals.map(({ label, value, isPrimary }) => (
        <div key={label} className={cn('signal', { primary: isPrimary })}>
          <span className='value'>{value}</span>
          <span className='label'>{label}</span>
        </div>
      ))}
    </div>
  );

  if (compact) {
    return (
      <div className={cn('blended-search-result compact', { 'collection-tile': isCollection })}>
        <div className='blended-media'>
          {media}
          <span className='blended-rank'>#{rank}</span>
          <span className={cn('blended-type-chip', type)}>{isCollection ? 'Collection' : 'Item'}</span>
        </div>
        <div className='blended-main'>
          {titleNode}
          {subtitle && <div className='blended-subtitle'>{subtitle}</div>}
          {signalNodes}
        </div>
      </div>
    );
  }

  return (
    <div className='blended-search-result'>
      <div className='blended-rank'>#{rank}</div>
      {media}
      <div className='blended-main'>
        <div className='blended-top'>
          <span className={cn('blended-type-chip', type)}>{isCollection ? 'Collection' : 'Item'}</span>
          {titleNode}
        </div>
        {subtitle && <div className='blended-subtitle'>{subtitle}</div>}
        {signalNodes}
      </div>
    </div>
  );
};

const ItemResult = props => {
  const { item, position, movement } = props;
  const { title, brand, image, price, numPromoters, rank, similarity, blendedScore, brandAffinity, pricePersonalization, rankReasons, injected } = item;
  const link = getSafeExternalUrl(item.link);
  const content = (
    <>
      <div className='item-rank'>
        {position != null ? `#${position}` : rank != null ? `#${rank}` : '—'}
        {position != null && rank != null && rank !== position && <span className='item-retrieval-rank'>was #{rank}</span>}
        {movement === 'new' && <span className='item-movement new'>new</span>}
        {typeof movement === 'number' && movement !== 0 && (
          <span className={cn('item-movement', movement > 0 ? 'up' : 'down')}>{movement > 0 ? `↑${movement}` : `↓${-movement}`}</span>
        )}
      </div>
      {image ? <ShopmyImage source={image} alt={title || brand} /> : <div className='item-image-placeholder' />}
      <div className='item-main'>
        {brand && <div className='item-brand'>{brand}</div>}
        <div className='item-title'>{title || 'Untitled'}</div>
        <div className='item-meta'>
          {price != null && <span className='item-price'>{getPrettyPriceDisplay(price)}</span>}
          {!!numPromoters && <span className='item-promoters'>{numPromoters} promoters</span>}
          {injected && <span className='item-affinity injected'>injected</span>}
          {brandAffinity && <span className='item-affinity boost'>{brandAffinity} brand</span>}
          {pricePersonalization && <span className='item-affinity demote'>{pricePersonalization.replace('_', ' ')}</span>}
          {(rankReasons || []).map(reason => (
            <span key={reason} className={cn('item-affinity', RANK_REASON_CHIPS[reason]?.tone || 'neutral')}>
              {RANK_REASON_CHIPS[reason]?.label || reason.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
        <div className='item-signals'>
          <div className='signal primary'>
            <span className='value'>{blendedScore != null ? blendedScore.toFixed(3) : '—'}</span>
            <span className='label'>rank score</span>
          </div>
          <div className='signal'>
            <span className='value'>{similarity != null ? `${Math.round(similarity * 100)}%` : '—'}</span>
            <span className='label'>match</span>
          </div>
        </div>
      </div>
    </>
  );
  return link ? (
    <a className='item-search-result' href={link} target='_blank' rel='noopener noreferrer'>
      {content}
    </a>
  ) : (
    <div className='item-search-result'>{content}</div>
  );
};

const SearchResult = props => {
  const { result, passingScore, movement } = props;
  const {
    Collection_id,
    source,
    similarity,
    documentSimilarity,
    facetSimilarities,
    rankScore,
    facetMatch,
    effectiveScore,
    inStockPct,
    freshness,
    colorMatch,
    creatorAffinity,
    searchDescription,
    collageUrl,
    collectionName,
    collectionImage,
    collectionUpdatedAt,
    creatorName,
    creatorUsername,
    previewPins
  } = result;
  const image = collageUrl || collectionImage;
  const isPassing = effectiveScore != null && effectiveScore >= passingScore;
  const facetSpaceSignals = [
    { key: 'occasion', label: 'occ' },
    { key: 'aesthetic', label: 'vibe' },
    { key: 'items', label: 'items' }
  ].filter(({ key }) => facetSimilarities && key in facetSimilarities);
  return (
    <div className='collection-search-result'>
      <div className='result-top'>
        <div className='result-image'>{image ? <ShopmyImage source={image} alt={collectionName} /> : <div className='image-placeholder' />}</div>
        <div className='result-main'>
          <div className='result-name-row'>
            <a className='result-name' href={getCollectionUrlFromId(Collection_id)} target='_blank' rel='noopener noreferrer'>
              {collectionName || `Collection #${Collection_id}`}
            </a>
            {movement === 'new' && <span className='result-movement new'>new</span>}
            {typeof movement === 'number' && movement !== 0 && (
              <span className={cn('result-movement', movement > 0 ? 'up' : 'down')}>{movement > 0 ? `↑${movement}` : `↓${-movement}`}</span>
            )}
            {creatorAffinity && (
              <span className={cn('result-affinity', creatorAffinity)}>{creatorAffinity === 'circle' ? 'circle' : 'neighbor'}</span>
            )}
          </div>
          {source === 'concierge' && <span className='result-source-flag'>Concierge</span>}
          {creatorUsername && (
            <a className='result-creator' href={getCreatorProfileUrl(creatorUsername)} target='_blank' rel='noopener noreferrer'>
              {creatorName || creatorUsername}
            </a>
          )}
          {searchDescription && <div className='result-description'>{searchDescription}</div>}
          {collectionUpdatedAt && <div className='result-updated'>Updated {moment(collectionUpdatedAt).fromNow()}</div>}
        </div>
      </div>
      {!!previewPins?.length && (
        <div className='result-pins'>
          {previewPins.map(pin => (
            <a key={pin.id} className='result-pin' href={getSafeExternalUrl(pin.link) || undefined} target='_blank' rel='noopener noreferrer'>
              {pin.image ? <ShopmyImage source={pin.image} alt={pin.title} /> : <div className='pin-image-placeholder' />}
              <div className='pin-domain'>{pin.domain}</div>
              <div className='pin-title'>{pin.title || 'Untitled'}</div>
              {pin.price != null && <div className='pin-price'>{getPrettyPriceDisplay(pin.price)}</div>}
            </a>
          ))}
        </div>
      )}
      <div className='result-signals'>
        <div className='signal primary'>
          <span className='value'>{rankScore != null ? rankScore.toFixed(3) : '—'}</span>
          <span className='label'>rank</span>
        </div>
        <div className='signal'>
          <span className='value'>{Math.round(similarity * 100)}%</span>
          <span className='label'>match</span>
        </div>
        {documentSimilarity != null && documentSimilarity !== similarity && (
          <div className='signal'>
            <span className='value'>{Math.round(documentSimilarity * 100)}%</span>
            <span className='label'>doc</span>
          </div>
        )}
        {facetSpaceSignals.map(({ key, label }) => (
          <div key={key} className='signal'>
            <span className='value'>{facetSimilarities[key] != null ? `${Math.round(facetSimilarities[key] * 100)}%` : '—'}</span>
            <span className='label'>{label}</span>
          </div>
        ))}
        <div className={cn('signal', { boosted: facetMatch != null && facetMatch > 0.5, demoted: facetMatch != null && facetMatch < 0.5 })}>
          <span className='value'>{facetMatch != null ? `${Math.round(facetMatch * 100)}%` : '—'}</span>
          <span className='label'>facets</span>
        </div>
        <div className={cn('signal', { passing: isPassing })}>
          <span className='value'>{effectiveScore != null ? Number(effectiveScore).toFixed(1) : '—'}</span>
          <span className='label'>score</span>
        </div>
        <div className='signal'>
          <span className='value'>{inStockPct != null ? `${Math.round(inStockPct * 100)}%` : '—'}</span>
          <span className='label'>in stock</span>
        </div>
        <div className='signal'>
          <span className='value'>{freshness != null ? freshness.toFixed(2) : '—'}</span>
          <span className='label'>fresh</span>
        </div>
        {colorMatch && (
          <div className={cn('signal', { boosted: colorMatch === 'match', demoted: colorMatch === 'mismatch' })}>
            <span className='value'>{colorMatch === 'match' ? 'yes' : colorMatch === 'mismatch' ? 'no' : '—'}</span>
            <span className='label'>color</span>
          </div>
        )}
      </div>
    </div>
  );
};

CollectionSearch.propTypes = {
  passingScore: PropTypes.number.isRequired
};

ExpansionPanel.propTypes = {
  title: PropTypes.string.isRequired,
  intent: PropTypes.string,
  extraChips: PropTypes.arrayOf(PropTypes.string),
  badge: PropTypes.string,
  children: PropTypes.node
};

CollectionExpansionBody.propTypes = {
  query: PropTypes.string,
  expandedQuery: PropTypes.string,
  queryFacets: PropTypes.object
};

ItemExpansionBody.propTypes = {
  query: PropTypes.string,
  expansion: PropTypes.object
};

SearchResult.propTypes = {
  result: PropTypes.object.isRequired,
  passingScore: PropTypes.number.isRequired,
  movement: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['new'])])
};

ItemResult.propTypes = {
  item: PropTypes.object.isRequired,
  position: PropTypes.number,
  movement: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['new'])])
};

BlendedResult.propTypes = {
  entry: PropTypes.object.isRequired,
  rank: PropTypes.number.isRequired,
  compact: PropTypes.bool,
  blendWeights: PropTypes.shape({ item: PropTypes.number, vibe: PropTypes.number })
};

export default CollectionSearch;
